import {
  Page,
  Text,
  View,
  StyleSheet,
  Document,
  Image,
} from "@react-pdf/renderer";
import logo from "@/img/lansing-logo.jpeg";
import DateHelper from "@/app/helpers/DateHelper";
import RebatePdfDataProps from "@/app/interfaces/props/RebatePdfDataProps";
import CommonHelper from "@/app/helpers/CommonHelper";
import TableSettingsVendor from "@/app/interfaces/TableSettingsVendor";
import VendorPdfSettingsHelper from "@/app/helpers/VendorPdfSettingsHelper";

const DEFAULT_COLUMNS: TableSettingsVendor[] = [
  { label: "Invoice Date", key: "invoiceDate", width: "10%" },
  { label: "Order Number", key: "orderNumber", width: "15%" },
  { label: "Lansing Account #", key: "harveyAccount", width: "15%" },
  { label: "Account Name", key: "harveyAccountName", width: "25%" },
  { label: "Product Purchased", key: "productPurchased", width: "35%" },
];

const styles = StyleSheet.create({
  page: {
    padding: 30,
    paddingBottom: 50,
    fontSize: 8,
    fontFamily: "Helvetica",
  },
  header: { marginBottom: 20, textAlign: "center" },
  title: { fontSize: 12, fontWeight: "bold", marginBottom: 10 },
  logo: { width: 120, height: 30, marginBottom: 10, alignSelf: "center" },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderBottomWidth: 1,
    borderColor: "#bfbfbf",
  },
  tableRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderColor: "#bfbfbf",
    minHeight: 18,
  },
  tableHeader: { backgroundColor: "#f0f0f0", fontWeight: "bold" },
  col: {
    borderRightWidth: 1,
    borderColor: "#bfbfbf",
    padding: 3,
    textAlign: "center",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: "center",
  },
  footerText: {
    fontSize: 8,
    fontStyle: "italic",
    color: "#000",
    lineHeight: 1.4,
  },
});

const RebateSummaryPDF = ({ data }: { data: RebatePdfDataProps }) => {
  const columns =
    VendorPdfSettingsHelper.tablePDFSettings[
      data.vendor as keyof typeof VendorPdfSettingsHelper.tablePDFSettings
    ] || DEFAULT_COLUMNS;

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <Text style={styles.title}>
            {VendorPdfSettingsHelper.tablePDFNameMapper[data.vendor] ??
              data.vendor}{" "}
            Products Purchased {DateHelper.toFullMonthYear(data.monthYear)}
          </Text>
        </View>

        <View style={styles.table}>
          <View fixed style={[styles.tableRow, styles.tableHeader]}>
            {columns.map((col: TableSettingsVendor, i: number) => (
              <Text
                key={i}
                style={[
                  styles.col,
                  { width: col.width },
                  i === columns.length - 1 ? { borderRightWidth: 0 } : {},
                ]}
              >
                {col.label}
              </Text>
            ))}
          </View>

          {data.items.map((item, index) => (
            <View key={index} style={styles.tableRow} wrap={false}>
              {columns.map((col: TableSettingsVendor, i: number) => {
                const value = item[col.key];

                let displayValue;

                if (col.key === "invoiceDate") {
                  displayValue = value
                    ? new Date(value as string).toLocaleDateString()
                    : "-";
                } else if (col.key === "amount") {
                  displayValue = CommonHelper.addDolarSign(value as number);
                } else {
                  displayValue = String(value ?? "-");
                }

                return (
                  <Text
                    key={i}
                    style={[
                      styles.col,
                      { width: col.width },
                      i === columns.length - 1 ? { borderRightWidth: 0 } : {},
                    ]}
                  >
                    {displayValue}
                  </Text>
                );
              })}
            </View>
          ))}
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            Lansing Building Products cannot guarantee absolute accuracy or be
            held accountable for reporting discrepancies. Please review your
            submissions monthly for complete accuracy.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default RebateSummaryPDF;
